import React, { useState, useEffect } from 'react';
import {
  Award,
  BookOpen,
  Target,
  CheckCircle,
  Users,
  Calendar,
  Clock,
  DollarSign,
  FileText,
  GraduationCap,
  TrendingUp,
  Mail,
  Building2,
  Send,
  Loader2,
  Shield,
  Lightbulb,
  User,
  Presentation,
  Network,
  Globe,
  ArrowRight,
  Video,
  FileCheck,
  Briefcase
} from 'lucide-react';
import { useFormSubmit } from '../hooks/useFormSubmit';

const CertificacionesConocer = () => {
  const [datosBancarios, setDatosBancarios] = useState(null);
  const [isLoadingBancarios, setIsLoadingBancarios] = useState(true);
  const [config, setConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  
  const { loading, success, error, submitForm, resetForm } = useFormSubmit('certificacion-conocer');
  
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    institucion: '',
    estandar: '',
    mensaje: '',
    acepto: false
  });

  // Cargar datos bancarios
  useEffect(() => {
    const fetchDatosBancarios = async () => {
      try {
        const response = await fetch(`/data/generales.json?v=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Error al cargar datos bancarios');
        const data = await response.json();
        setDatosBancarios(data.datosBancarios);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setIsLoadingBancarios(false);
      }
    };
    fetchDatosBancarios();
  }, []);

  // Cargar configuración de certificaciones
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`/data/certificaciones-conocer.json?v=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) throw new Error('Error al cargar configuración');
        const data = await response.json();
        setConfig(data);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setIsLoadingConfig(false);
      }
    };
    fetchConfig();
  }, []);

  // Estándares de competencia disponibles
  const estandares = [
    {
      codigo: 'EC0076',
      titulo: 'Evaluación de la competencia de candidatos con base en Estándares de Competencia',
      descripcion: 'Estándar base para evaluar cualquier estándar del CONOCER. Establece los criterios para evaluar con fines de certificación, asegurando objetividad, validez y confiabilidad.',
      icono: Shield,
      color: 'from-blue-500 to-blue-600'
    },
    {
      codigo: 'EC0217.01',
      titulo: 'Impartición de cursos de formación del capital humano de manera presencial grupal',
      descripcion: 'Certifica a facilitadores de procesos formativos presenciales, asegurando claridad, dominio del tema, metodología adecuada e interacción efectiva.',
      icono: Presentation,
      color: 'from-green-500 to-green-600'
    },
    {
      codigo: 'EC0301',
      titulo: 'Diseño de cursos de formación del capital humano de manera presencial grupal',
      descripcion: 'Certifica el diseño de cursos presenciales grupales, elaboración de instrumentos de evaluación y manuales, garantizando procesos formativos estructurados y alineados.',
      icono: FileText,
      color: 'from-purple-500 to-purple-600'
    },
    {
      codigo: 'EC0366',
      titulo: 'Desarrollo de cursos de formación en línea',
      descripcion: 'Comprende la creación de contenidos digitales, diseño instruccional para entornos virtuales e integración de recursos tecnológicos para experiencias de aprendizaje efectivas.',
      icono: Video,
      color: 'from-orange-500 to-orange-600'
    },
    {
      codigo: 'EC0185',
      titulo: 'Impartición de acciones de capacitación presencial con enfoque incluyente',
      descripcion: 'Certifica la impartición de capacitación bajo enfoque incluyente, con planificación accesible, conducción de grupos aplicando principios de inclusión y evaluación considerando la diversidad.',
      icono: Users,
      color: 'from-red-500 to-red-600'
    }
  ];

  // Plan de trabajo
  const planTrabajo = [
    {
      numero: '1',
      titulo: 'Recepción y Validación de Registros',
      descripcion: 'Validación inicial de documentación y requisitos para iniciar el proceso de certificación.',
      icono: FileCheck,
      color: 'blue'
    },
    {
      numero: '2',
      titulo: 'Logística del Proceso Formativo',
      descripcion: 'Organización de sesiones teórico-prácticas en modalidad virtual (síncrona y asíncrona), con grupos flexibles según demanda y zonas horarias.',
      detalles: [
        'Capacitación: 10 horas virtuales',
        'Sesiones síncronas en vivo por videoconferencia',
        'Materiales descargables y guías instruccionales',
        'Evaluación simulada para medir dominio y reforzar seguridad'
      ],
      icono: Calendar,
      color: 'green'
    },
    {
      numero: '3',
      titulo: 'Evaluación Final y Certificación',
      descripcion: 'Proceso completo de evaluación y emisión de certificado oficial.',
      detalles: [
        'Evaluación práctica y documental conforme al Estándar',
        'Emisión de Cédula de Competencia Laboral (Dictamen)',
        'Programación de certificación oficial ante el CONOCER',
        'Certificado entregado en aprox. 30 días hábiles'
      ],
      icono: Award,
      color: 'purple'
    }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.acepto) {
      alert('Debes aceptar el Aviso de Privacidad para continuar');
      return;
    }
    // Agregar emails destino desde config
    const formDataWithEmails = {
      ...formData,
      emails: config?.emails || ['certificaciones@cenid.org']
    };
    await submitForm(formDataWithEmails);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <Award className="w-12 h-12 text-[#e87729]" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold">
                Certificación de Competencias Laborales
              </h1>
              <p className="text-xl text-green-100 mt-2">
                CENID A.C. en vinculación con AICEC, Servicios profesionales de capacitación, evaluación y certificación de competencias laborales, 
              en apego a los lineamientos y normativa del Sistema Nacional de Competencias, con validez oficial 
              otorgada por la SEP–CONOCER.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Sobre AICEC */}
        <section className="mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-[#0b742f]">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-[#0b742f]" />
                Acerca de AICEC
              </h2>
              <div className="h-1 w-24 bg-[#e87729] rounded"></div>
            </div>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    <strong>AICEC - Agencia Independiente de Certificación de Competencias</strong>
                  </p>
                  <div className="space-y-2 text-gray-700">
                    <p><strong>Cédula:</strong> EI0089-ECE469-20</p>
                    <p><strong>Adscripción:</strong> Instituto de Certificación y Desarrollo de Competencias Certilab (ECE469-20)</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
              <div className="flex items-start gap-3">
                <Target className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-green-900 mb-2">Nuestro Propósito</p>
                  <p className="text-gray-700 leading-relaxed">
                    Fortalecer la calidad educativa y la excelencia docente, a través de la profesionalización del 
                    capital humano, garantizando que personal académico, técnico y operativo cuente con las competencias 
                    necesarias para la formación, evaluación y acompañamiento de estudiantes en entornos presenciales, 
                    mixtos y virtuales.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Información sobre Certificación CONOCER */}
        <section className="mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-[#e87729]">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <Lightbulb className="w-8 h-8 text-[#e87729]" />
                ¿Qué es la certificación CONOCER?
              </h2>
              <div className="h-1 w-24 bg-[#0b742f] rounded"></div>
            </div>

            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                La <strong>certificación CONOCER</strong> es un documento oficial emitido por el gobierno de México para validar las habilidades, destrezas y conocimientos que una persona ha adquirido a lo largo de su vida laboral o académica, sin importar si tiene estudios formales o no.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <h3 className="font-semibold text-blue-900 mb-3">¿Qué es la certificación CONOCER?</h3>
                <p>
                  En lenguaje sencillo, es un <strong>"diploma oficial" que certifica lo que sabes hacer</strong> en un área específica, basado en estándares definidos para un alto desempeño en el trabajo. El Consejo Nacional de Normalización y Certificación de Competencias Laborales (<a href="https://conocer.gob.mx/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline font-semibold">CONOCER</a>) es la entidad paraestatal, sectorizada a la Secretaría de Educación Pública (<strong>SEP</strong>), responsable de este proceso.
                </p>
              </div>

              <p>
                La certificación se obtiene a través de un proceso de evaluación que mide las capacidades productivas del individuo en un contexto laboral específico, a diferencia de un título académico tradicional que se centra en los conocimientos teóricos.
              </p>

              <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
                <h3 className="font-semibold text-green-900 mb-3">Validez oficial</h3>
                <p className="mb-4">
                  La certificación CONOCER tiene <strong>validez oficial plena en todo el territorio mexicano y cuenta con el respaldo de la SEP</strong>.
                </p>

                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <strong>Reconocimiento Nacional:</strong> Es un documento legal y oficial que acredita formalmente tu experiencia y te posiciona mejor en el mercado laboral.
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <strong>Reconocimiento Internacional:</strong> Gracias a diversos acuerdos comerciales, estas certificaciones pueden ser reconocidas en otros países.
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <strong>Beneficios laborales:</strong> Sirve como un excelente respaldo para personas con mucha experiencia que no cuentan con un título universitario, facilitando la obtención de mejores empleos o contratos, y brindando mayor seguridad a empleadores y clientes sobre tus capacidades.
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <strong>Verificable:</strong> Cada certificado cuenta con elementos de seguridad como códigos QR y firmas oficiales, permitiendo verificar su autenticidad en la plataforma oficial del gobierno, el <a href="https://conocer.gob.mx/registro-nacional-personas-competencias-certificadas/" target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900 underline font-semibold">Registro Nacional de Personas con Competencias Certificadas (RENAP)</a>.
                    </div>
                  </li>
                </ul>
              </div>

              <p>
                En resumen, la certificación CONOCER es una herramienta que convierte tu experiencia y habilidades prácticas en un <strong>documento oficial y verificable</strong> respaldado por la máxima autoridad educativa de México, abriendo puertas a nuevas oportunidades profesionales.
              </p>
            </div>
          </div>
        </section>

        {/* Estándares de Competencia */}
        <section className="mb-12">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3 flex items-center gap-3">
              <FileText className="w-8 h-8 text-[#0b742f]" />
              Estándares de Competencia Disponibles
            </h2>
            <div className="h-1 w-24 bg-[#e87729] rounded mb-4"></div>
            <p className="text-gray-600 text-lg">
              Certificaciones de la línea educativa que impartimos y evaluamos
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {estandares.map((estandar, index) => {
              const IconComponent = estandar.icono;
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-gray-200 hover:border-[#0b742f]"
                >
                  <div className={`bg-gradient-to-r ${estandar.color} p-6 text-white`}>
                    <div className="flex items-start gap-4">
                      <IconComponent className="w-10 h-10 flex-shrink-0" />
                      <div>
                        <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold inline-block mb-2">
                          {estandar.codigo}
                        </div>
                        <h3 className="font-bold text-xl leading-tight">
                          {estandar.titulo}
                        </h3>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-700 leading-relaxed">
                      {estandar.descripcion}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Plan de Trabajo */}
        <section className="mb-12">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3 flex items-center gap-3">
              <Briefcase className="w-8 h-8 text-[#0b742f]" />
              Plan de Trabajo
            </h2>
            <div className="h-1 w-24 bg-[#e87729] rounded mb-4"></div>
            <p className="text-gray-600 text-lg">
              Acompañamiento personalizado, logística y proceso ante el CONOCER. 
              Garantizando calidad, orden y trazabilidad en cada etapa.
            </p>
          </div>

          <div className="space-y-6">
            {planTrabajo.map((etapa, index) => {
              const IconComponent = etapa.icono;
              const colorMap = {
                blue: 'bg-blue-500 border-blue-500',
                green: 'bg-green-500 border-green-500',
                purple: 'bg-purple-500 border-purple-500'
              };
              
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden border-l-4 border-gray-300 hover:shadow-xl transition-all duration-300"
                >
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`${colorMap[etapa.color]} text-white rounded-full w-12 h-12 flex items-center justify-center text-2xl font-bold flex-shrink-0`}>
                        {etapa.numero}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <IconComponent className={`w-6 h-6 text-${etapa.color}-600`} />
                          <h3 className="text-xl font-bold text-gray-900">
                            {etapa.titulo}
                          </h3>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          {etapa.descripcion}
                        </p>
                      </div>
                    </div>
                    
                    {etapa.detalles && (
                      <div className="ml-16 mt-4 bg-gray-50 rounded-lg p-4">
                        <ul className="space-y-2">
                          {etapa.detalles.map((detalle, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <CheckCircle className={`w-5 h-5 text-${etapa.color}-600 flex-shrink-0 mt-0.5`} />
                              <span className="text-gray-700">{detalle}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Costos del Servicio */}
        {!isLoadingConfig && config?.mostrarCostos && (
          <section className="mb-12">
            <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-2xl shadow-2xl p-8 md:p-12">
              <div className="text-center mb-8">
                <DollarSign className="w-16 h-16 mx-auto mb-4 text-[#e87729]" />
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Costo del Servicio
                </h2>
                <div className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 inline-block mb-4">
                  <p className="text-sm font-semibold">TODO INCLUIDO</p>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8 border-2 border-white/20">
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-2 mb-2">
                    <span className="text-6xl font-bold">${config?.costo?.monto?.toLocaleString('es-MX') || '3,500'}</span>
                    <span className="text-2xl">.00</span>
                  </div>
                  <p className="text-xl text-green-100">{config?.costo?.moneda || 'MXN'} {config?.costo?.descripcion || 'por participante'}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {(config?.incluye || []).map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

            {/* Datos Bancarios */}
            {!isLoadingBancarios && datosBancarios && (
              <div className="bg-white/95 text-gray-900 rounded-xl p-6 shadow-lg">
                <h3 className="font-bold text-xl mb-4 text-center">Datos para Pago</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 mb-1">Banco:</p>
                    <p className="font-semibold">{datosBancarios.banco}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Titular:</p>
                    <p className="font-semibold">{datosBancarios.titular}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Número de Cuenta:</p>
                    <p className="font-mono font-semibold">{datosBancarios.numeroCuenta}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">CLABE:</p>
                    <p className="font-mono font-semibold">{datosBancarios.clabe}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Sucursal:</p>
                    <p className="font-semibold">{datosBancarios.sucursal}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">SWIFT:</p>
                    <p className="font-mono font-semibold">{datosBancarios.swift}</p>
                  </div>
                </div>
              </div>
            )}
            </div>
          </section>
        )}

        {/* Formulario de Solicitud */}
        <section className="mb-12">
          <div className="bg-white rounded-xl shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Mail className="w-8 h-8 text-[#0b742f]" />
                Solicita Información
              </h2>
              <p className="text-gray-600 mb-6">Los campos marcados con * son obligatorios</p>

              {/* Mensajes de éxito/error */}
              {success && (
                <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-green-800">¡Solicitud enviada exitosamente!</h3>
                      <p className="text-green-700 text-sm">Nos pondremos en contacto contigo pronto.</p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Mail className="w-6 h-6 text-red-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-red-800">Error al enviar</h3>
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nombre */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nombre completo *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors outline-none disabled:bg-gray-100"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Email y Teléfono */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Correo electrónico *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors outline-none disabled:bg-gray-100"
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors outline-none disabled:bg-gray-100"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Institución */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Institución *
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="institucion"
                      value={formData.institucion}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors outline-none disabled:bg-gray-100"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Estándar de interés */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Estándar de interés *
                  </label>
                  <select
                    name="estandar"
                    value={formData.estandar}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors outline-none disabled:bg-gray-100"
                    required
                    disabled={loading}
                  >
                    <option value="">Selecciona un estándar</option>
                    {estandares.map((est, idx) => (
                      <option key={idx} value={est.codigo}>
                        {est.codigo} - {est.titulo}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mensaje */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mensaje adicional (opcional)
                  </label>
                  <textarea
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#0b742f] focus:ring-2 focus:ring-[#0b742f]/20 transition-colors outline-none disabled:bg-gray-100 resize-none"
                    disabled={loading}
                  />
                </div>

                {/* Checkbox Privacidad */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="acepto"
                    checked={formData.acepto}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 text-[#0b742f] border-gray-300 rounded focus:ring-[#0b742f]"
                    required
                    disabled={loading}
                  />
                  <label className="text-sm text-gray-700">
                    Acepto el{' '}
                    <a href="/nosotros/aviso-privacidad" className="text-[#0b742f] hover:underline font-semibold">
                      Aviso de Privacidad
                    </a>{' '}
                    y autorizo el uso de mis datos para los fines aquí establecidos *
                  </label>
                </div>

                {/* Botón Submit */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading || !formData.acepto}
                    className="w-full bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-6 h-6" />
                        Enviar Solicitud
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl shadow-2xl p-8 md:p-12 text-center">
            <Award className="w-16 h-16 mx-auto mb-6 text-yellow-300" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              ¿Listo para certificar tus competencias?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Obtén tu certificación oficial CONOCER y fortalece tu perfil profesional con validez ante la SEP
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="mailto:inscripcionespostgrados@cenid.org"
                className="inline-flex items-center gap-3 bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105"
              >
                <Mail className="w-6 h-6" />
                Contáctanos
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CertificacionesConocer;
